/**
 * Drop-in replacement for the supabase-js PostgREST query builder.
 *
 * The app's data now lives in Neon Postgres, which the browser cannot (and
 * must not) reach directly. `supabase.from('table')...` calls are therefore
 * serialized and sent to `POST /api/db/query`, where the backend applies the
 * org / role scoping that Supabase Row-Level-Security used to provide and runs
 * the query with psycopg2.
 *
 * The public surface mirrors supabase-js so the existing call sites keep
 * working unchanged:
 *   select / insert / upsert / update / delete
 *   eq / neq / gt / gte / lt / lte / like / ilike / is / in / contains / not / or / match / filter
 *   order / limit / range / single / maybeSingle / throwOnError
 *   await ... -> { data, error, count, status, statusText }
 */

function readViewingOrg() {
  try { return localStorage.getItem('viewing_org_id'); } catch (_) { return null; }
}

export class GatewayQueryBuilder {
  constructor(table, { api, getToken }) {
    this._api = api;
    this._getToken = getToken;
    this._plan = {
      table,
      action: 'select',
      select: '*',
      filters: [],
      or: [],
      order: [],
      limit: null,
      offset: null,
      single: null,
      values: undefined,
      onConflict: undefined,
      ignoreDuplicates: false,
      count: null,
    };
    this._throwOnError = false;
  }

  // ---- verbs ----------------------------------------------------------------
  select(columns = '*', opts = {}) {
    this._plan.select = (columns || '*').replace(/\s+/g, ' ');
    if (opts && opts.count) this._plan.count = opts.count;
    return this;
  }

  insert(values, opts = {}) {
    this._plan.action = 'insert';
    this._plan.values = values;
    if (opts && opts.count) this._plan.count = opts.count;
    return this;
  }

  upsert(values, opts = {}) {
    this._plan.action = 'upsert';
    this._plan.values = values;
    this._plan.onConflict = opts?.onConflict;
    this._plan.ignoreDuplicates = !!opts?.ignoreDuplicates;
    return this;
  }

  update(values, opts = {}) {
    this._plan.action = 'update';
    this._plan.values = values;
    if (opts && opts.count) this._plan.count = opts.count;
    return this;
  }

  delete(opts = {}) {
    this._plan.action = 'delete';
    if (opts && opts.count) this._plan.count = opts.count;
    return this;
  }

  // ---- filters --------------------------------------------------------------
  _f(column, op, value) {
    this._plan.filters.push({ column, op, value });
    return this;
  }
  eq(c, v) { return this._f(c, 'eq', v); }
  neq(c, v) { return this._f(c, 'neq', v); }
  gt(c, v) { return this._f(c, 'gt', v); }
  gte(c, v) { return this._f(c, 'gte', v); }
  lt(c, v) { return this._f(c, 'lt', v); }
  lte(c, v) { return this._f(c, 'lte', v); }
  like(c, v) { return this._f(c, 'like', v); }
  ilike(c, v) { return this._f(c, 'ilike', v); }
  is(c, v) { return this._f(c, 'is', v); }
  in(c, values) { return this._f(c, 'in', Array.isArray(values) ? values : [values]); }
  contains(c, v) { return this._f(c, 'contains', v); }
  not(c, op, v) { return this._f(c, `not.${op}`, v); }
  filter(c, op, v) { return this._f(c, op, v); }
  match(obj) { Object.entries(obj || {}).forEach(([k, v]) => this.eq(k, v)); return this; }
  or(expr) { if (expr) this._plan.or.push(String(expr)); return this; }

  // ---- modifiers ------------------------------------------------------------
  order(column, opts = {}) {
    const ascending = opts?.ascending === undefined ? true : !!opts.ascending;
    this._plan.order.push({ column, desc: !ascending, nullsFirst: opts?.nullsFirst ?? null });
    return this;
  }
  limit(n) { this._plan.limit = n; return this; }
  range(from, to) { this._plan.offset = from; this._plan.limit = to - from + 1; return this; }
  single() { this._plan.single = 'single'; return this; }
  maybeSingle() { this._plan.single = 'maybe'; return this; }
  throwOnError() { this._throwOnError = true; return this; }

  // ---- execution ------------------------------------------------------------
  async _run() {
    let token = null;
    try { token = await this._getToken(); } catch (_) { token = null; }
    if (!token) {
      return this._finish({ data: null, count: null, error: { message: 'Not authenticated', code: '401', details: null, hint: null }, status: 401, statusText: 'Unauthorized' });
    }
    const viewingOrg = readViewingOrg();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(viewingOrg ? { 'X-Viewing-Org-Id': viewingOrg } : {}),
    };
    let resp;
    try {
      resp = await fetch(`${this._api}/db/query`, { method: 'POST', headers, body: JSON.stringify(this._plan) });
    } catch (e) {
      return this._finish({ data: null, count: null, error: { message: e?.message || 'Network error', code: 'FETCH', details: null, hint: null }, status: 0, statusText: 'Network error' });
    }
    if (!resp.ok) {
      let msg = `${resp.status}`;
      try { const j = await resp.json(); msg = j?.detail || j?.error?.message || msg; } catch (_) { /* ignore */ }
      return this._finish({ data: null, count: null, error: { message: String(msg), code: String(resp.status), details: null, hint: null }, status: resp.status, statusText: resp.statusText });
    }
    const body = await resp.json();
    return this._finish({
      data: body?.data ?? null,
      count: body?.count ?? null,
      error: body?.error ?? null,
      status: body?.error ? 400 : 200,
      statusText: body?.error ? 'Bad Request' : 'OK',
    });
  }

  _finish(result) {
    if (result.error && this._throwOnError) {
      const err = new Error(result.error.message || 'Query failed');
      Object.assign(err, result.error);
      throw err;
    }
    return result;
  }

  // Thenable so `await supabase.from(...).select(...)` works like supabase-js.
  then(onFulfilled, onRejected) {
    return this._run().then(onFulfilled, onRejected);
  }
  catch(onRejected) { return this._run().catch(onRejected); }
  finally(onFinally) { return this._run().finally(onFinally); }
}

/**
 * Patch a supabase-js client so `client.from(table)` routes through the backend gateway.
 * Auth / storage / realtime APIs on the client are untouched.
 */
export function installGatewayFrom(client, api) {
  const getToken = async () => {
    const { data: { session } } = await client.auth.getSession();
    return session?.access_token || null;
  };
  client.from = (table) => new GatewayQueryBuilder(table, { api, getToken });
  return client;
}
